/**
 * Collection Content Module
 *
 * Loads and applies collection-specific content from content-map.json.
 * Deep merges defaults with collection-specific overrides.
 * Automatically applies content to hero, products, and reviews sections on collection pages.
 *
 * Exposes: window.CollectionContent
 */

(function() {
  'use strict';

  let mapCache = null;
  let mapPromise = null;
  let reviewsCache = null;
  let reviewsPromise = null;

  /**
   * Append Shopify CDN width parameter to image URL
   * @param {string} url - Original image URL
   * @param {number} width - Desired width in pixels
   * @returns {string} URL with width parameter
   */
  function getSizedImageUrl(url, width) {
    if (!url || !width) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  /**
   * Get the content map URL from the DOM.
   * Looks for #content-map-data first (rendered by theme.liquid),
   * then falls back to #content-map-url for legacy compatibility.
   * @returns {string|null} The URL or null if not found
   */
  function getContentMapUrl() {
    // Primary: #content-map-data (theme.liquid wraps the snippet output)
    const primaryEl = document.getElementById('content-map-data');
    if (primaryEl) {
      try { return JSON.parse(primaryEl.textContent.trim()); } catch (e) { /* fall through */ }
    }
    // Legacy fallback: #content-map-url
    const legacyEl = document.getElementById('content-map-url');
    if (legacyEl) {
      try { return JSON.parse(legacyEl.textContent.trim()); } catch (e) { /* fall through */ }
    }
    console.warn('CollectionContent: Content map URL element not found');
    return null;
  }

  /**
   * Load and cache the collection map JSON
   * Fetches from URL (preloaded in theme.liquid for performance)
   * @param {string} url - URL to content-map.json (optional, uses DOM if not provided)
   * @returns {Promise<Object|null>} The parsed map or null on error
   */
  async function loadCollectionMap(url) {
    if (mapCache) return mapCache;
    if (mapPromise) return mapPromise;

    // Get URL from DOM if not provided
    const fetchUrl = url || getContentMapUrl();
    if (!fetchUrl) {
      console.warn('CollectionContent: Content map URL not found');
      return null;
    }

    mapPromise = fetch(fetchUrl, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { mapCache = data; return data; })
      .catch(() => null);

    return mapPromise;
  }

  /**
   * Load and cache the reviews map JSON from reviews.json
   * @param {string} [url] - URL to reviews.json (optional, auto-detected from DOM)
   * @returns {Promise<Object|null>} The parsed reviews map or null on error
   */
  async function loadReviewsMap(url) {
    if (reviewsCache) return reviewsCache;
    if (reviewsPromise) return reviewsPromise;

    const fetchUrl = url ||
      document.querySelector('[data-reviews-map-url]')?.dataset.reviewsMapUrl;
    if (!fetchUrl) return null;

    reviewsPromise = fetch(fetchUrl, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { reviewsCache = data; return data; })
      .catch(() => null);

    return reviewsPromise;
  }

  /**
   * Get reviews for a given context from the reviews map.
   * Checks collections, then products, then falls back to defaults.
   * @param {Object|null} reviewsMap - The loaded reviews map
   * @param {string} [collectionHandle] - Collection handle
   * @param {string} [productHandle] - Product handle
   * @returns {Object|null} Reviews object with heading/subheading/items
   */
  function getReviews(reviewsMap, collectionHandle, productHandle) {
    if (!reviewsMap) return null;
    if (collectionHandle && reviewsMap.collections?.[collectionHandle]) {
      return reviewsMap.collections[collectionHandle];
    }
    if (productHandle && reviewsMap.products?.[productHandle]) {
      return reviewsMap.products[productHandle];
    }
    return reviewsMap.defaults || null;
  }

  /**
   * Get the Shopify URL for a product
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @returns {string} The Shopify URL
   */
  function getProductUrl(map, productHandle) {
    return map?.products?.[productHandle]?.shopify_url || `/products/${productHandle}`;
  }

  /**
   * Deep merge defaults with collection-specific overrides
   * Arrays are replaced entirely, not merged item-by-item
   * @param {Object} defaults - The default values
   * @param {Object} overrides - Collection-specific overrides
   * @returns {Object} Merged result
   */
  function deepMerge(defaults, overrides) {
    if (!overrides) return defaults;
    const result = { ...defaults };

    for (const key of Object.keys(overrides)) {
      if (overrides[key] && typeof overrides[key] === 'object' && !Array.isArray(overrides[key])) {
        result[key] = deepMerge(defaults[key] || {}, overrides[key]);
      } else if (overrides[key] !== undefined && overrides[key] !== null) {
        result[key] = overrides[key];
      }
    }

    return result;
  }

  /**
   * Get merged content for a collection handle
   * Falls back to defaults if collection not found
   * @param {Object} map - The loaded content map
   * @param {string} handle - Collection handle (e.g., "mothers-day")
   * @returns {Object|null} Merged content or null if map is invalid
   */
  function getCollectionContent(map, handle) {
    if (!map) return null;
    const defaults = map.defaults || {};
    const specific = map.collections?.[handle] || {};
    return deepMerge(defaults, specific);
  }

  /**
   * Extract only image filenames from a slides array (filters out video objects).
   * @param {Array} slides - Slides array (strings and/or objects)
   * @returns {string[]} Image filenames only
   */
  function getImageFilenames(slides) {
    if (!Array.isArray(slides)) return [];
    return slides.filter(s => typeof s === 'string');
  }

  /**
   * Get images for a product in a collection context
   * Uses collection-specific product_slides if available, falls back to product slides
   * Filters out video entries, returns only image filenames.
   * @param {Object} map - The loaded content map
   * @param {string} collectionHandle - Collection handle
   * @param {string} productHandle - Product handle
   * @returns {string[]} Array of image filenames
   */
  function getProductImages(map, collectionHandle, productHandle) {
    if (!map) return [];

    // Try collection-specific product_slides first (skip if flagged for generic images)
    if (!map.collections?.[collectionHandle]?.use_generic_images) {
      const collectionSlides = map.collections?.[collectionHandle]?.product_slides?.[productHandle];
      if (collectionSlides && collectionSlides.length > 0) {
        return getImageFilenames(collectionSlides);
      }
    }

    // Fall back to product slides
    return getImageFilenames(map.products?.[productHandle]?.slides || []);
  }

  /**
   * Get product metadata
   * @param {Object} map - The loaded content map
   * @param {string} productHandle - Product handle
   * @returns {Object|null} Product metadata
   */
  function getProductMeta(map, productHandle) {
    return map?.products?.[productHandle] || null;
  }

  /**
   * Apply hero section content
   * @param {Element} section - Hero section element
   * @param {Object} hero - Hero content object
   */
  function applyHeroContent(section, hero) {
    if (!hero) return;

    // Main heading and subheading
    const heading = section.querySelector('[data-hero-heading]');
    const subheading = section.querySelector('[data-hero-subheading]');
    const button = section.querySelector('[data-hero-button]');
    const finePrint = section.querySelector('[data-hero-fine-print]');

    if (heading && hero.heading) heading.innerHTML = hero.heading;
    if (subheading && hero.subheading) subheading.innerHTML = hero.subheading;
    if (button && hero.button_label) {
      button.textContent = hero.button_label;
      if (hero.button_link === '#products') {
        button.removeAttribute('href');
        button.setAttribute('role', 'button');
        button.addEventListener('click', function () {
          var target = document.querySelector('[data-products-section]');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } else if (hero.button_link) {
        button.href = hero.button_link;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('role');
      }
    }
    if (finePrint && hero.fine_print) finePrint.textContent = hero.fine_print;

    // Proof items
    if (hero.proofs && hero.proofs.length) {
      hero.proofs.forEach((proof, i) => {
        const proofEl = section.querySelector(`[data-hero-proof="${i}"]`);
        if (proofEl) proofEl.textContent = proof;
      });
    }

    // Dynamic slides are built separately by buildHomepageSlides() or buildHeroSlidesFromProducts()
    // using the hero.slides array from content-map.json
  }

  /**
   * Build dynamic slides from product images
   * @param {Element} section - Hero section element
   * @param {Object} hero - Hero config with overlay_heading, overlay_subheading
   * @param {string[]} images - Array of image filenames
   * @param {string} collectionHandle - Collection handle for context
   */
  /**
   * Build a single hero slide DOM element (image or video).
   * Shared by buildDynamicSlides, buildHeroSlidesFromProducts, and buildHomepageSlides.
   */
  function buildSlideElement({ slideIdBase, index, totalSlides, slide, assetsBase, assetsVersion, isFirst, firstImageFilename }) {
    const isVideo = slide.type === 'video';

    const slideEl = document.createElement('div');
    slideEl.className = 'hero-split__slide slideshow__slide grid__item grid--1-col slider__slide';
    slideEl.id = `${slideIdBase}-${index + 1}`;
    slideEl.setAttribute('role', 'group');
    slideEl.setAttribute('aria-roledescription', 'Slide');
    slideEl.setAttribute('aria-label', `${index + 1} of ${totalSlides}`);
    if (isVideo) slideEl.dataset.heroVideoSlide = '';
    slideEl.tabIndex = -1;

    const mediaEl = document.createElement('div');
    mediaEl.className = 'hero-split__media media';

    if (isVideo) {
      const video = document.createElement('video');
      video.src = assetsBase + slide.src + (assetsVersion ? '?v=' + assetsVersion : '');
      video.autoplay = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.preload = 'metadata';
      video.className = 'hero-split__video';
      if (firstImageFilename) {
        video.poster = getSizedImageUrl(assetsBase + firstImageFilename + (assetsVersion ? '?v=' + assetsVersion : ''), 800) + '&format=webp';
      }
      mediaEl.appendChild(video);
    } else {
      const img = document.createElement('img');
      const baseUrl = assetsBase + slide.filename + (assetsVersion ? '?v=' + assetsVersion : '');
      img.src = getSizedImageUrl(baseUrl, 800) + '&format=webp';
      img.srcset = [
        getSizedImageUrl(baseUrl, 400) + '&format=webp 400w',
        getSizedImageUrl(baseUrl, 800) + '&format=webp 800w',
        getSizedImageUrl(baseUrl, 1200) + '&format=webp 1200w'
      ].join(', ');
      img.sizes = '(max-width: 749px) 100vw, 50vw';
      img.alt = slide.overlayHeading || `Slide ${index + 1}`;
      img.loading = isFirst ? 'eager' : 'lazy';
      if (isFirst) img.fetchPriority = 'high';
      img.width = 1500;
      img.height = 1875;
      mediaEl.appendChild(img);
    }

    const oh = slide.overlayHeading;
    const os = slide.overlaySubheading;
    if (oh || os) {
      const overlay = document.createElement('div');
      overlay.className = 'hero-split__media-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.dataset.heroSlideOverlay = index;

      const overlayInner = document.createElement('div');
      overlayInner.className = 'hero-split__media-overlay-inner';

      if (oh) {
        const ohEl = document.createElement('p');
        ohEl.className = 'hero-split__media-overlay-heading';
        ohEl.dataset.overlayHeading = '';
        ohEl.textContent = oh;
        overlayInner.appendChild(ohEl);
      }
      if (os) {
        const osEl = document.createElement('p');
        osEl.className = 'hero-split__media-overlay-subheading';
        osEl.dataset.overlaySubheading = '';
        osEl.textContent = os;
        overlayInner.appendChild(osEl);
      }

      const debugFn = document.createElement('p');
      debugFn.className = 'debug-asset-filename';
      debugFn.textContent = slide.filename || slide.src || '';
      overlayInner.appendChild(debugFn);

      overlay.appendChild(overlayInner);
      mediaEl.appendChild(overlay);
    }

    slideEl.appendChild(mediaEl);
    return slideEl;
  }

  /**
   * Build a single dot indicator button for the hero carousel.
   */
  function buildDot({ index, totalSlides, sliderId, isFirst }) {
    const dot = document.createElement('button');
    dot.className = `slider-counter__link slider-counter__link--dots link${isFirst ? ' slider-counter__link--active' : ''}`;
    dot.setAttribute('aria-label', `Load slide ${index + 1} of ${totalSlides}`);
    dot.setAttribute('aria-controls', sliderId);
    const dotSpan = document.createElement('span');
    dotSpan.className = 'dot';
    dot.appendChild(dotSpan);
    return dot;
  }

  /**
   * Two-phase slide rendering: show first slide immediately, defer the rest
   * until the first image loads (or 3s timeout).
   */
  function renderSlidesInTwoPhases({ slides, slidesContainer, dotsContainer, slideIdBase, sliderId, assetsBase, assetsVersion, section, onAllAppended, firstImageFilename }) {
    const totalSlides = slides.length;
    if (totalSlides === 0) return;

    // Phase 1: build and append first slide immediately
    const firstSlideData = slides[0];
    const firstSlideEl = buildSlideElement({
      slideIdBase, index: 0, totalSlides, slide: firstSlideData,
      assetsBase, assetsVersion, isFirst: true, firstImageFilename
    });
    slidesContainer.appendChild(firstSlideEl);
    if (dotsContainer) {
      dotsContainer.appendChild(buildDot({ index: 0, totalSlides, sliderId, isFirst: true }));
    }

    // Phase 2: append remaining slides after first image loads
    const remaining = slides.slice(1);

    function appendRemaining() {
      remaining.forEach(function (slide, i) {
        var idx = i + 1;
        slidesContainer.appendChild(buildSlideElement({
          slideIdBase, index: idx, totalSlides, slide,
          assetsBase, assetsVersion, isFirst: false, firstImageFilename
        }));
        if (dotsContainer) {
          dotsContainer.appendChild(buildDot({ index: idx, totalSlides, sliderId, isFirst: false }));
        }
      });
      reinitializeSlideshow(section, slidesContainer, dotsContainer);
      if (onAllAppended) onAllAppended();
    }

    if (remaining.length === 0) {
      reinitializeSlideshow(section, slidesContainer, dotsContainer);
      if (onAllAppended) onAllAppended();
      return;
    }

    var firstImg = firstSlideEl.querySelector('img');
    if (firstImg) {
      if (firstImg.complete) {
        appendRemaining();
      } else {
        var done = false;
        firstImg.addEventListener('load', function () { if (!done) { done = true; appendRemaining(); } });
        firstImg.addEventListener('error', function () { if (!done) { done = true; appendRemaining(); } });
        setTimeout(function () { if (!done) { done = true; appendRemaining(); } }, 3000);
      }
    } else {
      appendRemaining();
    }
  }

  function buildDynamicSlides(section, hero, images, collectionHandle) {
    const slidesContainer = section.querySelector('[data-hero-slides-container]');
    const dotsContainer = section.querySelector('[data-hero-dots-container]');

    if (!slidesContainer || !images || images.length === 0) return;

    const assetsBase = slidesContainer.dataset.assetsBase || '';
    const assetsVersion = slidesContainer.dataset.assetsVersion || '';
    const sliderId = slidesContainer.id || 'Slider-dynamic';
    const slideIdBase = sliderId.replace(/^Slider-/, 'Slide-');

    slidesContainer.innerHTML = '';
    if (dotsContainer) dotsContainer.innerHTML = '';

    const slides = images.map(function (imageFilename) {
      return {
        type: 'image',
        filename: imageFilename,
        overlayHeading: hero.overlay_heading || '',
        overlaySubheading: hero.overlay_subheading || ''
      };
    });

    renderSlidesInTwoPhases({
      slides, slidesContainer, dotsContainer, slideIdBase, sliderId,
      assetsBase, assetsVersion, section
    });
  }

  /**
   * Apply products section content
   * @param {Element} section - Products section element
   * @param {Object} products - Products content object
   */
  function applyProductsContent(section, products) {
    if (!products) return;

    const heading = section.querySelector('[data-products-heading]');
    const subheading = section.querySelector('[data-products-subheading]');

    if (heading && products.heading) heading.textContent = products.heading;
    if (subheading && products.subheading) subheading.textContent = products.subheading;

    // Reorder products if product_handles is specified
    if (products.product_handles && products.product_handles.length > 0) {
      reorderProducts(products.product_handles);
    }
  }

  /**
   * Reorder product cards in the DOM based on specified order
   * Products not in the list are hidden
   * Content-map now uses virtual handles directly (fridge-magnets, photo-magnets)
   * @param {string[]} productHandles - Array of product handles in desired order (from content-map)
   */
  function reorderProducts(productHandles) {
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-product-card]'));
    if (!cards.length) return;

    // Create a map of handle -> card element
    const cardMap = new Map();
    cards.forEach(card => {
      const handle = card.dataset.productHandle;
      if (handle) cardMap.set(handle, card);
    });

    // Remove all cards from the grid temporarily
    cards.forEach(card => card.remove());

    // Track how many products are actually shown
    let visibleCount = 0;

    // Re-add cards in the specified order
    productHandles.forEach(handle => {
      const card = cardMap.get(handle);
      if (card) {
        card.style.display = ''; // Ensure visible
        grid.appendChild(card);
        cardMap.delete(handle); // Mark as processed
        visibleCount++;
      }
    });

    // Hide any remaining cards not in the list
    cardMap.forEach(card => {
      card.style.display = 'none';
      grid.appendChild(card);
    });

    // Update product count display
    updateProductCount(visibleCount);
  }

  /**
   * Update the product count display to reflect visible products
   * @param {number} count - Number of visible products
   */
  function updateProductCount(count) {
    const countElements = document.querySelectorAll('.product-count__text');
    countElements.forEach(el => {
      // Simple replacement: "X products" format
      el.textContent = `${count} product${count !== 1 ? 's' : ''}`;
    });
  }

  /**
   * Apply reviews section content
   * @param {Element} section - Reviews section element
   * @param {Object} reviews - Reviews content object
   */
  /* ── Shared SVG templates for review cards ────────────────── */

  const REVIEW_STAR_SVG =
    '<svg class="review-card__star review-card__star--filled" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">' +
    '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>' +
    '</svg>';

  const REVIEW_AVATAR_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>' +
    '<circle cx="12" cy="7" r="4"></circle>' +
    '</svg>';

  const REVIEW_VERIFIED_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">' +
    '<path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-.997-6l7.07-7.071-1.414-1.414-5.656 5.657-2.829-2.829-1.414 1.414L11.003 16z"/>' +
    '</svg>';

  /**
   * Build a single review card slide element
   */
  function buildReviewCard(item, index, total, sectionId, cardColorScheme) {
    const slide = document.createElement('div');
    slide.className = 'reviews-carousel__slide slideshow__slide';
    slide.id = `Slide-${sectionId}-${index + 1}`;
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'Slide');
    slide.setAttribute('aria-label', `${index + 1} of ${total}`);
    slide.setAttribute('data-review-item', String(index));

    const card = document.createElement('div');
    card.className = `review-card color-${cardColorScheme}`;

    /* Stars */
    const stars = document.createElement('div');
    stars.className = 'review-card__stars';
    stars.setAttribute('aria-label', `${item.rating || 5} out of 5 stars`);
    const starCount = item.rating || 5;
    for (let s = 0; s < starCount; s++) {
      stars.innerHTML += REVIEW_STAR_SVG;
    }
    card.appendChild(stars);

    /* Quote */
    const quote = document.createElement('blockquote');
    quote.className = 'review-card__text';
    quote.textContent = `\u201C${item.text}\u201D`;
    card.appendChild(quote);

    /* Author */
    const author = document.createElement('div');
    author.className = 'review-card__author';

    const avatar = document.createElement('div');
    avatar.className = 'review-card__avatar review-card__avatar--placeholder';
    avatar.innerHTML = REVIEW_AVATAR_SVG;
    author.appendChild(avatar);

    const info = document.createElement('div');
    info.className = 'review-card__author-info';

    if (item.name) {
      const nameEl = document.createElement('span');
      nameEl.className = 'review-card__name';
      nameEl.textContent = item.name;
      info.appendChild(nameEl);
    }

    const verified = document.createElement('span');
    verified.className = 'review-card__verified';
    verified.innerHTML = `${REVIEW_VERIFIED_SVG} Verified Buyer`;
    info.appendChild(verified);

    author.appendChild(info);
    card.appendChild(author);

    /* Product purchased */
    if (item.product) {
      const prodLine = document.createElement('p');
      prodLine.className = 'review-card__product';
      prodLine.textContent = `Purchased: ${item.product}`;
      card.appendChild(prodLine);
    }

    slide.appendChild(card);
    return slide;
  }

  /**
   * Build dot navigation for review slides
   */
  function buildReviewDots(count, sectionId) {
    const dotsWrapper = document.createElement('div');
    dotsWrapper.className = 'reviews-carousel__dots slideshow__control-wrapper';
    for (let i = 0; i < count; i++) {
      const btn = document.createElement('button');
      btn.className = 'slider-counter__link slider-counter__link--dots link';
      btn.setAttribute('aria-label', `Load slide ${i + 1} of ${count}`);
      btn.setAttribute('aria-controls', `Slider-${sectionId}`);
      const dot = document.createElement('span');
      dot.className = 'dot';
      btn.appendChild(dot);
      dotsWrapper.appendChild(btn);
    }
    return dotsWrapper;
  }

  /**
   * Apply reviews content to a reviews-carousel section.
   * Updates heading, subheading, rebuilds review cards and dots.
   * @param {Element} section - The reviews-carousel section element
   * @param {Object} reviews - Reviews content object with heading, subheading, items
   */
  function applyReviewsContent(section, reviews) {
    if (!reviews) return;

    const heading = section.querySelector('[data-reviews-heading]');
    const subheading = section.querySelector('[data-reviews-subheading]');

    if (heading && reviews.heading) heading.innerHTML = reviews.heading;
    if (subheading && reviews.subheading) subheading.textContent = reviews.subheading;

    /* If there are items, rebuild the card slides */
    if (!reviews.items || !reviews.items.length) return;

    const sliderContainer = section.querySelector('[data-reviews-container]');
    if (!sliderContainer) return;

    /* Derive section ID */
    let sectionId = '';
    if (sliderContainer.id) {
      sectionId = sliderContainer.id.replace('Slider-', '');
    }

    /* Detect card color scheme from existing cards */
    const existingCard = section.querySelector('.review-card');
    let cardColorScheme = 'scheme-1';
    if (existingCard) {
      const match = existingCard.className.match(/color-(scheme-\d+)/);
      if (match) cardColorScheme = match[1];
    }

    /* Clear and rebuild slides */
    sliderContainer.innerHTML = '';
    const items = reviews.items;
    for (let i = 0; i < items.length; i++) {
      sliderContainer.appendChild(buildReviewCard(items[i], i, items.length, sectionId, cardColorScheme));
    }

    /* Rebuild dot navigation */
    const controlsEl = section.querySelector('.reviews-carousel__controls');
    if (controlsEl) {
      const oldDots = controlsEl.querySelector('.reviews-carousel__dots');
      const newDots = buildReviewDots(items.length, sectionId);
      if (oldDots) {
        controlsEl.replaceChild(newDots, oldDots);
      }
    }

    /* Re-init slideshow component */
    const slideshowEl = section.querySelector('slideshow-component');
    if (slideshowEl) {
      /* 1. Re-query slide elements (SliderComponent.resetPages) */
      if (typeof slideshowEl.resetPages === 'function') {
        slideshowEl.resetPages();
      }

      /* 2. Re-bind dot click listeners */
      const controlWrapper = slideshowEl.querySelector('.slider-buttons');
      if (controlWrapper) {
        const newLinks = Array.from(controlWrapper.querySelectorAll('.slider-counter__link'));
        slideshowEl.sliderControlLinksArray = newLinks;
        newLinks.forEach((link) => {
          link.addEventListener('click', slideshowEl.linkToSlide.bind(slideshowEl));
        });
      }

      /* 3. Update first-item reference and current page */
      slideshowEl.sliderFirstItemNode = slideshowEl.slider
        ? slideshowEl.slider.querySelector('.slideshow__slide')
        : null;
      slideshowEl.currentPage = 1;

      /* 4. Reset scroll to first slide */
      if (slideshowEl.slider) {
        slideshowEl.slider.scrollLeft = 0;
      }

      /* 5. Re-set slide visibility */
      if (typeof slideshowEl.setSlideVisibility === 'function') {
        slideshowEl.setSlideVisibility();
      }

      /* 6. Restart autoplay if enabled */
      if (slideshowEl.slider && slideshowEl.slider.getAttribute('data-autoplay') === 'true') {
        if (typeof slideshowEl.pause === 'function') slideshowEl.pause();
        slideshowEl.autoplaySpeed = (parseInt(slideshowEl.slider.dataset.speed, 10) || 5) * 1000;
        if (typeof slideshowEl.play === 'function') slideshowEl.play();
      }
    }
  }

  /**
   * Apply content to DOM elements using data-* selectors (legacy support)
   * @param {string} rootSelector - CSS selector for the root element (e.g., "body")
   * @param {Object} content - Merged content object
   * @param {string} assetsBase - Base URL for assets (e.g., "/cdn/shop/t/1/assets/")
   */
  function applyContent(rootSelector, content, assetsBase, assetsVersion) {
    if (!content) return;

    // Legacy hero section support
    const heroEyebrow = document.querySelector(`${rootSelector} [data-hero-eyebrow]`);
    const heroHeadline = document.querySelector(`${rootSelector} [data-hero-headline]`);
    const heroSubheading = document.querySelector(`${rootSelector} [data-hero-subheading]`);
    const heroImage = document.querySelector(`${rootSelector} [data-hero-image]`);

    if (heroEyebrow && content.hero?.eyebrow) heroEyebrow.textContent = content.hero.eyebrow;
    if (heroHeadline && content.hero?.headline) heroHeadline.textContent = content.hero.headline;
    if (heroSubheading && content.hero?.subheading) heroSubheading.textContent = content.hero.subheading;
    if (heroImage && content.hero?.image) {
      heroImage.src = assetsBase + content.hero.image + (assetsVersion ? '?v=' + assetsVersion : '');
      heroImage.closest('[data-hero-image-wrapper]')?.classList.remove('hidden');
    }

    // Legacy why section (3 bullets)
    const whyItems = document.querySelectorAll(`${rootSelector} [data-why-item]`);
    if (content.why && whyItems.length) {
      content.why.forEach((item, i) => {
        if (whyItems[i]) {
          const title = whyItems[i].querySelector('[data-why-title]');
          const body = whyItems[i].querySelector('[data-why-body]');
          if (title) title.textContent = item.title;
          if (body) body.textContent = item.body;
        }
      });
    }

    // Legacy how section (3 steps)
    const howItems = document.querySelectorAll(`${rootSelector} [data-how-item]`);
    if (content.how && howItems.length) {
      content.how.forEach((item, i) => {
        if (howItems[i]) {
          const title = howItems[i].querySelector('[data-how-title]');
          const body = howItems[i].querySelector('[data-how-body]');
          if (title) title.textContent = item.title;
          if (body) body.textContent = item.body || '';
        }
      });
    }

    // Legacy reviews section (2 quotes)
    const reviewItems = document.querySelectorAll(`${rootSelector} [data-review-item]`);
    if (content.reviews && reviewItems.length) {
      content.reviews.forEach((item, i) => {
        if (reviewItems[i]) {
          const quote = reviewItems[i].querySelector('[data-review-quote]');
          const author = reviewItems[i].querySelector('[data-review-author]');
          if (quote) quote.textContent = item.quote;
          if (author) author.textContent = item.author ? `— ${item.author}` : '';
        }
      });
    }
  }

  /**
   * Get the occasion handle for product imagery
   * In the new structure, the collection handle IS the occasion
   * @param {Object} map - The loaded content map
   * @param {string} collectionHandle - Collection handle
   * @returns {string} Occasion handle (same as collection handle)
   */
  function getOccasionHandle(map, collectionHandle) {
    // In the new structure, collection handle is the occasion
    return collectionHandle;
  }

  /**
   * Auto-initialize collection content on page load
   * Detects collection handle from data-collection-handle attributes
   */
  async function initCollectionContent() {
    // Find collection handle from any section with data-collection-handle
    const sectionWithHandle = document.querySelector('[data-collection-handle]');
    if (!sectionWithHandle) return;

    const collectionHandle = sectionWithHandle.dataset.collectionHandle;
    if (!collectionHandle) return;

    // Load the content map
    // Priority: 1) data attribute from Liquid (correct theme URL), 2) fallback paths
    const dataAttrUrl = document.querySelector('[data-collection-map-url]')?.dataset.collectionMapUrl;

    // Try multiple possible asset URLs - data attribute first
    const possibleUrls = [
      dataAttrUrl,
      '/assets/content-map.json'
    ].filter(Boolean);

    let map = null;
    for (const url of possibleUrls) {
      map = await loadCollectionMap(url);
      if (map) break;
    }

    if (!map) {
      console.warn('CollectionContent: Could not load content-map.json');
      return;
    }

    // Get merged content for this collection
    const content = getCollectionContent(map, collectionHandle);
    if (!content) return;

    // Apply content to each section type
    const heroSection = document.querySelector('[data-hero-section]');
    if (heroSection) {
      applyHeroContent(heroSection, content.hero);

      // Prefer explicit collection hero slides (skip if flagged for generic images)
      const useGenericImages = map.collections?.[collectionHandle]?.use_generic_images;
      if (!useGenericImages && content.hero?.slides && content.hero.slides.length > 0) {
        buildHomepageSlides(heroSection, content.hero, map);
      } else if (map.homepage?.hero?.slides && map.homepage.hero.slides.length > 0) {
        // Fallback: use homepage hero slides with collection overlay text
        const fallbackHero = { ...map.homepage.hero, ...content.hero, slides: map.homepage.hero.slides };
        buildHomepageSlides(heroSection, fallbackHero, map);
      } else {
        // Ultimate fallback: auto-build from collection product handles
        const productHandles = content.products_section?.product_handles ||
          ['fridge-magnets', 'photo-magnets', 'calendar-magnets', 'puzzle-magnets'];
        buildHeroSlidesFromProducts(heroSection, content.hero || {}, map, productHandles, collectionHandle);
      }
    }

    const productsSection = document.querySelector('[data-products-section]');
    if (productsSection) {
      applyProductsContent(productsSection, content.products_section);
    }

    const reviewsSection = document.querySelector('[data-reviews-section]');
    if (reviewsSection) {
      const reviewsMap = await loadReviewsMap();
      const reviews = getReviews(reviewsMap, collectionHandle) || content.reviews;
      if (reviews) applyReviewsContent(reviewsSection, reviews);
    }
  }

  /**
   * Build hero slide order from product handles
   * Returns an array of { productHandle, imageIndex } objects
   * Order: fm-1, pm-1, cal-1, std-1 (if present), puz-1, fm-2, pm-2
   *
   * @param {string[]} productHandles - Array of product handles to include
   * @returns {Array<{productHandle: string, imageIndex: number}>} Slide specifications
   */
  function buildHeroSlideOrder(productHandles) {
    const handles = productHandles || [];
    const slides = [];

    // Helper to check if a product is in the list
    const has = (handle) => handles.includes(handle);

    // Round 1: first image from each product in order
    if (has('fridge-magnets')) slides.push({ productHandle: 'fridge-magnets', imageIndex: 0 });
    if (has('photo-magnets')) slides.push({ productHandle: 'photo-magnets', imageIndex: 0 });
    if (has('calendar-magnets')) slides.push({ productHandle: 'calendar-magnets', imageIndex: 0 });
    if (has('save-the-date')) slides.push({ productHandle: 'save-the-date', imageIndex: 0 });
    if (has('puzzle-magnets')) slides.push({ productHandle: 'puzzle-magnets', imageIndex: 0 });

    // Round 2: second image from fridge and photo
    if (has('fridge-magnets')) slides.push({ productHandle: 'fridge-magnets', imageIndex: 1 });
    if (has('photo-magnets')) slides.push({ productHandle: 'photo-magnets', imageIndex: 1 });

    return slides;
  }

  /**
   * Get hero image for a product at a specific index
   * Tries occasion-specific product_slides first, falls back to product slides.
   * Filters out video entries.
   *
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @param {number} imageIndex - 0-based image index
   * @param {string|null} collectionHandle - Collection handle for occasion-specific images
   * @returns {string} Image filename
   */
  function getHeroImage(map, productHandle, imageIndex, collectionHandle) {
    // Try collection-specific product_slides first (skip if flagged for generic images)
    if (collectionHandle && !map.collections?.[collectionHandle]?.use_generic_images) {
      const collectionSlides = map.collections?.[collectionHandle]?.product_slides?.[productHandle];
      if (collectionSlides) {
        const images = getImageFilenames(collectionSlides);
        if (images[imageIndex]) return images[imageIndex];
      }
    }

    // Fall back to product slides (images only)
    const productSlides = map.products?.[productHandle]?.slides || [];
    const images = getImageFilenames(productSlides);
    return images[imageIndex] || images[0] || '';
  }

  /**
   * Build hero slides for any page (homepage or collection)
   * Uses the standard slide order based on product handles
   *
   * @param {Element} section - Hero section element
   * @param {Object} hero - Hero config with overlay content
   * @param {Object} map - The full content map
   * @param {string[]} productHandles - Products to include in slides
   * @param {string|null} collectionHandle - Collection handle for occasion-specific images
   */
  function buildHeroSlidesFromProducts(section, hero, map, productHandles, collectionHandle) {
    const slidesContainer = section.querySelector('[data-hero-slides-container]');
    const dotsContainer = section.querySelector('[data-hero-dots-container]');

    if (!slidesContainer) return;

    const assetsBase = slidesContainer.dataset.assetsBase || '';
    const assetsVersion = slidesContainer.dataset.assetsVersion || '';
    const sliderId = slidesContainer.id || 'Slider-dynamic';
    const slideIdBase = sliderId.replace(/^Slider-/, 'Slide-');

    const slideSpecs = buildHeroSlideOrder(productHandles);
    if (slideSpecs.length === 0) return;

    slidesContainer.innerHTML = '';
    if (dotsContainer) dotsContainer.innerHTML = '';

    const slides = slideSpecs.map(function (spec) {
      const imageFilename = getHeroImage(map, spec.productHandle, spec.imageIndex, collectionHandle);
      const productMeta = map.products?.[spec.productHandle] || {};
      return {
        type: 'image',
        filename: imageFilename || '',
        overlayHeading: hero?.overlay_heading || productMeta.overlay_heading || productMeta.title || '',
        overlaySubheading: hero?.overlay_subheading || productMeta.overlay_subheading || ''
      };
    }).filter(function (s) { return s.filename; });

    renderSlidesInTwoPhases({
      slides, slidesContainer, dotsContainer, slideIdBase, sliderId,
      assetsBase, assetsVersion, section
    });
  }

  /**
   * Build hero slides from an explicit slides array (supports video + image).
   * Works for both the homepage and collection pages.
   *
   * Slides in content-map.json are now:
   *   - string = image filename (e.g. "c-mothers-day-fm--01.jpg")
   *   - object { type:"video", src, overlay_heading?, overlay_subheading? }
   *
   * Auto-fill: if explicit slides have fewer than 7 image slides, additional
   * images are pulled from the collection/product defaults automatically.
   *
   * @param {Element} section - Hero section element
   * @param {Object} hero - Hero config with slides array, overlay_heading/subheading
   * @param {Object} map - The full content map
   */
  function buildHomepageSlides(section, hero, map) {
    const slidesContainer = section.querySelector('[data-hero-slides-container]');
    const dotsContainer = section.querySelector('[data-hero-dots-container]');

    if (!slidesContainer || !hero?.slides || hero.slides.length === 0) return;

    const assetsBase = slidesContainer.dataset.assetsBase || '';
    const assetsVersion = slidesContainer.dataset.assetsVersion || '';
    const sliderId = slidesContainer.id || 'Slider-dynamic';
    const slideIdBase = sliderId.replace(/^Slider-/, 'Slide-');
    const collectionHandle = section.dataset.collectionHandle || null;
    const TARGET_IMAGE_SLIDES = 7;

    // ── Normalize explicit slides ──
    const resolvedSlides = [];  // { type, filename?, src?, overlayHeading, overlaySubheading }
    const usedImages = new Set();

    hero.slides.forEach(slide => {
      if (typeof slide === 'string') {
        resolvedSlides.push({
          type: 'image',
          filename: slide,
          overlayHeading: hero.overlay_heading || '',
          overlaySubheading: hero.overlay_subheading || ''
        });
        usedImages.add(slide);
      } else if (slide && typeof slide === 'object' && slide.type === 'video' && slide.src) {
        resolvedSlides.push({
          type: 'video',
          src: slide.src,
          overlayHeading: slide.overlay_heading || '',
          overlaySubheading: slide.overlay_subheading || ''
        });
      }
    });

    // ── Auto-fill to reach TARGET_IMAGE_SLIDES ──
    let currentImageCount = resolvedSlides.filter(s => s.type === 'image').length;
    if (currentImageCount < TARGET_IMAGE_SLIDES) {
      const productHandles = collectionHandle
        ? (map.collections?.[collectionHandle]?.products_section?.product_handles ||
           ['fridge-magnets', 'photo-magnets', 'calendar-magnets', 'puzzle-magnets'])
        : ['fridge-magnets', 'photo-magnets', 'calendar-magnets', 'save-the-date', 'puzzle-magnets'];

      // Gather unused images from products
      for (const handle of productHandles) {
        if (currentImageCount >= TARGET_IMAGE_SLIDES) break;
        const images = getProductImages(map, collectionHandle, handle);
        for (const img of images) {
          if (currentImageCount >= TARGET_IMAGE_SLIDES) break;
          if (usedImages.has(img)) continue;
          resolvedSlides.push({
            type: 'image',
            filename: img,
            overlayHeading: hero.overlay_heading || '',
            overlaySubheading: hero.overlay_subheading || ''
          });
          usedImages.add(img);
          currentImageCount++;
        }
      }
    }

    // ── Clear and build DOM ──
    slidesContainer.innerHTML = '';
    if (dotsContainer) dotsContainer.innerHTML = '';

    const firstImageFilename = resolvedSlides.find(s => s.type === 'image')?.filename || '';

    renderSlidesInTwoPhases({
      slides: resolvedSlides,
      slidesContainer,
      dotsContainer,
      slideIdBase,
      sliderId,
      assetsBase,
      assetsVersion,
      section,
      firstImageFilename,
      onAllAppended: function () {
        slidesContainer.setAttribute('data-autoplay', 'false');
        setupHeroVideoObserver(slidesContainer);
        const autoplayConfig = resolvedSlides.map(s => ({ type: s.type }));
        setupHeroAutoplay(slidesContainer, dotsContainer, autoplayConfig);
      }
    });
  }

  /**
   * Custom autoplay for hero carousel with variable timing per slide type.
   * Video slides display for 8s, image slides for 5s.
   * Pauses on hover/focus and resumes on leave/blur.
   * @param {Element} slidesContainer - The scrollable slides wrapper
   * @param {Element} dotsContainer - The dots wrapper
   * @param {Array} slidesConfig - The slides array from content-map
   */
  function setupHeroAutoplay(slidesContainer, dotsContainer, slidesConfig) {
    const IMAGE_DELAY = 5000;
    const VIDEO_DELAY = 8000;
    let autoplayTimer = null;
    let paused = false;

    function getCurrentIndex() {
      const slideWidth = slidesContainer.children[0]?.clientWidth || 1;
      return Math.round(slidesContainer.scrollLeft / slideWidth);
    }

    function getDelayForSlide(index) {
      const config = slidesConfig[index];
      return (config && config.type === 'video') ? VIDEO_DELAY : IMAGE_DELAY;
    }

    function advanceSlide() {
      const totalSlides = slidesContainer.children.length;
      if (totalSlides <= 1) return;
      const current = getCurrentIndex();
      const next = (current + 1) % totalSlides;
      const slideWidth = slidesContainer.children[0]?.clientWidth || 0;
      slidesContainer.scrollTo({ left: slideWidth * next, behavior: 'smooth' });
    }

    function scheduleNext() {
      clearTimeout(autoplayTimer);
      if (paused) return;
      const currentIndex = getCurrentIndex();
      const delay = getDelayForSlide(currentIndex);
      autoplayTimer = setTimeout(() => {
        advanceSlide();
        // Next schedule happens on scroll event (after the scroll completes)
      }, delay);
    }

    // Re-schedule after each scroll settles
    let scrollDebounce = null;
    slidesContainer.addEventListener('scroll', () => {
      clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(() => {
        // Update dots
        if (dotsContainer) {
          const current = getCurrentIndex();
          dotsContainer.querySelectorAll('.slider-counter__link').forEach((d, i) => {
            d.classList.toggle('slider-counter__link--active', i === current);
          });
        }
        scheduleNext();
      }, 120);
    });

    // Pause on hover / focus
    const parentComponent = slidesContainer.closest('slideshow-component');
    const hoverTarget = parentComponent || slidesContainer;
    hoverTarget.addEventListener('mouseenter', () => { paused = true; clearTimeout(autoplayTimer); });
    hoverTarget.addEventListener('mouseleave', () => { paused = false; scheduleNext(); });
    hoverTarget.addEventListener('focusin', () => { paused = true; clearTimeout(autoplayTimer); });
    hoverTarget.addEventListener('focusout', () => { paused = false; scheduleNext(); });

    // Kick off
    scheduleNext();
  }

  /**
   * Observe hero slides and play/pause videos based on visibility.
   * - Plays a video when its slide scrolls into view (carousel navigation).
   * - Pauses videos when their slide scrolls out of view.
   * - Uses IntersectionObserver on individual slides within the scroll container.
   * @param {Element} slidesContainer - The scrollable slides wrapper
   */
  function setupHeroVideoObserver(slidesContainer) {
    const videoSlides = slidesContainer.querySelectorAll('[data-hero-video-slide]');
    if (!videoSlides.length) return;

    /* ── 1. Slide-level observer: play/pause as carousel scrolls ── */
    const slideObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, {
      root: slidesContainer,   // observe within the scroll container
      threshold: 0.6           // >60 % visible = "active slide"
    });

    videoSlides.forEach((slide) => slideObserver.observe(slide));

    /* ── 2. Section-level observer: pause all when hero scrolls offscreen ── */
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Pause every video in the hero when section is offscreen
          slidesContainer.querySelectorAll('video').forEach((v) => v.pause());
        }
      });
    }, {
      threshold: 0
    });

    const heroSection = slidesContainer.closest('[data-hero-section]');
    if (heroSection) sectionObserver.observe(heroSection);
  }

  /**
   * Reinitialize slideshow component after dynamic slide changes
   */
  function reinitializeSlideshow(section, slidesContainer, dotsContainer) {
    const slideshowComponent = section.querySelector('slideshow-component');
    if (!slideshowComponent) return;

    if (typeof slideshowComponent.resetPages === 'function') {
      slideshowComponent.resetPages();
    }

    // Pause built-in autoplay if it was running (we manage our own)
    if (typeof slideshowComponent.pause === 'function') {
      slideshowComponent.pause();
    }

    // Rebind dot click events
    if (dotsContainer) {
      const newDots = dotsContainer.querySelectorAll('button');
      newDots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const slideWidth = slidesContainer.children[0]?.clientWidth || 0;
          slidesContainer.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
          newDots.forEach((d, i) => {
            d.classList.toggle('slider-counter__link--active', i === index);
          });
        });
      });

      // Update dot state on scroll
      slidesContainer.addEventListener('scroll', () => {
        const scrollLeft = slidesContainer.scrollLeft;
        const slideWidth = slidesContainer.children[0]?.clientWidth || 1;
        const activeIndex = Math.round(scrollLeft / slideWidth);
        newDots.forEach((d, i) => {
          d.classList.toggle('slider-counter__link--active', i === activeIndex);
        });
      });
    }

    slidesContainer.scrollTo({ left: 0 });
  }

  /**
   * Enhance category carousel cards with images from content-map.json
   */
  async function enhanceCategoryCards() {
    const cards = document.querySelectorAll('[data-category-card]');
    if (!cards.length) return;

    // Load content map
    const mapUrl = cards[0].dataset.mediaMapUrl;
    if (!mapUrl) return;

    let map = null;
    try {
      map = await loadCollectionMap(mapUrl);
    } catch (e) {
      return;
    }

    if (!map?.products) return;

    for (const card of cards) {
      const productHandle = card.dataset.productHandle;
      const assetsBase = card.dataset.assetsBase || '';
      const assetsVersion = card.dataset.assetsVersion || '';

      if (!productHandle) continue;

      const productImages = getImageFilenames(map.products?.[productHandle]?.slides || []);
      if (!productImages.length) continue;

      // Update title and subtitle from content-map
      const productMeta = map.products?.[productHandle];
      if (productMeta) {
        const titleEl = card.querySelector('[data-card-title]');
        const subtitleEl = card.querySelector('[data-card-subtitle]');
        if (titleEl && productMeta.title) titleEl.textContent = productMeta.title;
        if (subtitleEl && productMeta.card_subtitle) subtitleEl.textContent = productMeta.card_subtitle;
      }

      // Update slide images with WebP format
      const slideImages = card.querySelectorAll('[data-category-slide-image]');
      slideImages.forEach((img, index) => {
        const slot = parseInt(img.dataset.slot, 10) || (index + 1);
        const imageFilename = productImages[slot - 1] || productImages[0];
        if (imageFilename) {
          img.src = getSizedImageUrl(assetsBase + imageFilename + (assetsVersion ? '?v=' + assetsVersion : ''), 400) + '&format=webp';
        }
      });
    }

    // Preserve occasion context in card links (e.g., on PDP cross-product grid)
    const occasionFromUrl = new URLSearchParams(window.location.search).get('occasion');
    const occasionFromPage = document.querySelector('[data-collection-handle]')?.dataset.collectionHandle;
    const occasion = occasionFromUrl || occasionFromPage || '';
    if (occasion) {
      for (const card of cards) {
        const links = card.querySelectorAll('a[href]');
        for (const link of links) {
          const href = link.getAttribute('href');
          if (href && href.startsWith('/products/') && !href.includes('occasion=')) {
            const sep = href.includes('?') ? '&' : '?';
            link.setAttribute('href', href + sep + 'occasion=' + occasion);
          }
        }
      }
    }
  }

  /**
   * Initialize homepage content from content-map.json
   */
  async function initHomepageContent() {
    // Check if we're on the homepage
    const isHomepage = window.location.pathname === '/' ||
                       document.body.classList.contains('template-index') ||
                       document.querySelector('[data-homepage]');

    if (!isHomepage) return;

    // Load the content map
    // Priority: 1) data attribute from Liquid (correct theme URL), 2) fallback paths
    const dataAttrUrl = document.querySelector('[data-collection-map-url]')?.dataset.collectionMapUrl;

    const possibleUrls = [
      dataAttrUrl,
      '/assets/content-map.json'
    ].filter(Boolean);

    let map = null;
    for (const url of possibleUrls) {
      map = await loadCollectionMap(url);
      if (map) break;
    }

    if (!map || !map.homepage) return;

    const homepage = map.homepage;

    // Apply hero content
    const heroSection = document.querySelector('[data-hero-section]');
    if (heroSection && homepage.hero) {
      applyHeroContent(heroSection, homepage.hero);

      // Prefer explicit slides array (supports video + image) over auto-generated
      if (homepage.hero.slides && homepage.hero.slides.length > 0) {
        buildHomepageSlides(heroSection, homepage.hero, map);
      } else {
        // Fallback: auto-build from product images
        const homepageProducts = ['fridge-magnets', 'photo-magnets', 'calendar-magnets', 'save-the-date', 'puzzle-magnets'];
        buildHeroSlidesFromProducts(heroSection, homepage.hero, map, homepageProducts, null);
      }
    }

    // Apply homepage reviews from reviews.json
    const reviewsSection = document.querySelector('[data-reviews-section]');
    if (reviewsSection) {
      const reviewsMap = await loadReviewsMap();
      const reviews = reviewsMap?.homepage || homepage.reviews;
      if (reviews) applyReviewsContent(reviewsSection, reviews);
    }
  }

  // Auto-initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCollectionContent();
      initHomepageContent();
      enhanceCategoryCards();
    });
  } else {
    initCollectionContent();
    initHomepageContent();
    enhanceCategoryCards();
  }

  // Expose API globally
  window.CollectionContent = {
    loadCollectionMap,
    loadReviewsMap,
    getCollectionContent,
    getReviews,
    getProductImages,
    getImageFilenames,
    getProductMeta,
    getProductUrl,
    applyContent,
    applyHeroContent,
    applyProductsContent,
    applyReviewsContent,
    buildDynamicSlides,
    buildHomepageSlides,
    buildHeroSlidesFromProducts,
    buildHeroSlideOrder,
    reorderProducts,
    getOccasionHandle,
    enhanceCategoryCards,
    initCollectionContent,
    initHomepageContent
  };

})();
